import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "../lib/api";
import { typeMeta } from "../lib/accountTypes";
import { X, WarningCircle, ArrowSquareOut, TreeStructure } from "@phosphor-icons/react";

// ── Searchable account picker (used for "Parent company") ──────────────
// Excludes the account itself and everything under it, so a loop can't be
// picked (the backend rejects loops too).
export default function AccountPicker({ value, onChange, excludeId, placeholder = "Search accounts…", testId = "account-picker" }) {
    const [accounts, setAccounts] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        endpoints.clients().then(({ data }) => setAccounts(data || [])).catch(() => {});
    }, []);
    useEffect(() => {
        function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const blocked = useMemo(() => {
        const out = new Set(excludeId ? [excludeId] : []);
        let grew = true;
        while (grew) {
            grew = false;
            accounts.forEach((a) => { if (a.parent_id && out.has(a.parent_id) && !out.has(a.id)) { out.add(a.id); grew = true; } });
        }
        return out;
    }, [accounts, excludeId]);

    const selected = accounts.find((a) => a.id === value);
    const options = accounts
        .filter((a) => !blocked.has(a.id))
        .filter((a) => !q || a.name?.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 8);

    if (selected && !open) {
        return (
            <div className="input flex items-center justify-between gap-2" data-testid={`${testId}-selected`}>
                <span className="truncate">{selected.name}</span>
                <button type="button" onClick={() => onChange(null)} className="text-[var(--rom-text-3)] hover:text-red-600" title="Remove parent" data-testid={`${testId}-clear`}>
                    <X size={12} weight="bold" />
                </button>
            </div>
        );
    }
    return (
        <div className="relative" ref={boxRef}>
            <input className="input" placeholder={placeholder} value={q} data-testid={testId}
                onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
            {open && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[var(--rom-border-strong)] rounded-[6px] shadow-lg max-h-64 overflow-y-auto">
                    {options.length === 0 && <div className="px-3 py-2 text-xs text-[var(--rom-text-3)]">No matching accounts</div>}
                    {options.map((a) => {
                        const Icon = typeMeta(a.type).icon;
                        return (
                            <button type="button" key={a.id} onClick={() => { onChange(a.id, a); setQ(""); setOpen(false); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`${testId}-option-${a.id}`}>
                                <Icon size={12} weight="bold" className="text-[var(--rom-text-3)] shrink-0" />
                                <span className="truncate">{a.name}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Account owner (sales rep) select — stores id + display name ────────
let ownersCache = null;
export function OwnerSelect({ ownerId, ownerName, onChange, testId = "owner-select" }) {
    const [owners, setOwners] = useState(ownersCache || []);
    useEffect(() => {
        if (ownersCache) return;
        endpoints.clientOwners().then(({ data }) => { ownersCache = data || []; setOwners(ownersCache); }).catch(() => {});
    }, []);
    const known = owners.some((o) => o.id === ownerId);
    return (
        <select className="input" value={ownerId || ""} data-testid={testId}
            onChange={(e) => {
                const o = owners.find((x) => x.id === e.target.value);
                onChange(o ? { owner_id: o.id, owner: o.name } : { owner_id: "", owner: "" });
            }}>
            <option value="">Unassigned</option>
            {!known && ownerId && <option value={ownerId}>{ownerName || "Unknown user"}</option>}
            {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
    );
}

// ── Live "this looks like an existing account" warning ─────────────────
export function DuplicateWarning({ name, email, phone, website, excludeId, onUseAsParent }) {
    const [hits, setHits] = useState([]);
    useEffect(() => {
        const enough = (name || "").trim().length >= 3 || (email || "").includes("@") || (phone || "").replace(/\D/g, "").length >= 7;
        if (!enough) { setHits([]); return undefined; }
        const t = setTimeout(() => {
            endpoints.checkClientDuplicates({ name: name || "", email: email || "", phone: phone || "", website: website || "", exclude_id: excludeId || undefined })
                .then(({ data }) => setHits(data || []))
                .catch(() => setHits([]));
        }, 400);
        return () => clearTimeout(t);
    }, [name, email, phone, website, excludeId]);

    if (!hits.length) return null;
    const strong = hits.some((h) => h.strength === "strong");
    return (
        <div className={`rounded-[6px] border p-3 text-xs space-y-2 ${strong ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="duplicate-warning">
            <div className="flex items-center gap-1.5 font-semibold">
                <WarningCircle size={14} weight="bold" className={strong ? "text-red-600" : "text-amber-600"} />
                {strong ? "This account probably already exists" : "Similar accounts on file"}
            </div>
            {hits.slice(0, 4).map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1.5" data-testid={`duplicate-hit-${h.id}`}>
                    <div className="min-w-0">
                        <div className="font-semibold truncate">{h.name}{h.parent_name && <span className="font-normal text-[var(--rom-text-3)]"> · under {h.parent_name}</span>}</div>
                        <div className="text-[var(--rom-text-3)]">{typeMeta(h.type).label} · {h.reasons.join(" · ")}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {onUseAsParent && (
                            <button type="button" onClick={() => onUseAsParent(h)} className="font-semibold text-[var(--rom-navy)] hover:underline flex items-center gap-1" data-testid={`duplicate-use-parent-${h.id}`}>
                                <TreeStructure size={12} weight="bold" /> Add under it
                            </button>
                        )}
                        <Link to={`/crm/accounts/${h.id}`} className="font-semibold text-[var(--rom-navy)] hover:underline flex items-center gap-1">
                            Open <ArrowSquareOut size={11} weight="bold" />
                        </Link>
                    </div>
                </div>
            ))}
        </div>
    );
}
